/**
 * The visit's Overview — `docs/visit-page.md` §7.4 – §7.8, §17.2, §17.4, §18
 * (do-not-substitute 4): **Days · Estimate vs actual · Ready for <next day> ·
 * For the crew | From the crew**, and the cards the other boards put in
 * their place — a one-day visit's row and the lock banner (Board 13 A), **The
 * series** and **Hours per visit** (Board 14), **Billed on the actuals** (T&M,
 * Board 13 A). Everything drawn is the server's `VisitOverview`.
 */
import type { ReactNode } from 'react'
import type {
  BilledOnActuals, CrewPhoto, DayChip, DayTile, Delta, EstimateCard, ForCrewSummary, FromCrewSummary,
  HoursPerVisitChart, OffThePlanRow, ReadyCard, ReadyRow, SeriesCard, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { EM_DASH } from '../lib/format.js'
import { StatusChip } from '../components/primitives.js'
import { Forecast } from '../activity/calendar/blocks.js'
import { BoardCard, CardLink, SmallButton } from './card.js'
import {
  COMMON, FOR_CREW, FROM_CREW, HEADER, MATERIALS, ONE_DAY, OVERVIEW, RECEIPTS, RENTALS, SERIES, SWAP, TM_BILLED,
} from './copy.js'
import { EvBar, MarginBar, ReadyBar } from './ev-bar.js'
import { Face, FaceRow } from './face.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { InternalChip } from './internal-chip.js'
import { SourceChip } from './crew-parts.js'
import {
  dayText, hours1, hoursBare, localTime, money, percent, signed, signedMoney, timeIn,
} from './format.js'

export interface OverviewHandlers {
  onOpen: (href: string) => void
  /** A day tile, or `?day=n` — Slice 6's day sheet. */
  onOpenDay?: (day: number) => void
  /** **Add a day** — Slice 6's popup. */
  onAddDay?: () => void
  /** Ready's **Order** — Slice 2's sheet. */
  onOrder?: (lineId: string) => void
  /** Ready's **Book it** — Slice 3's sheet. */
  onBook?: (rentalId: string) => void
  /** For the crew's **Preview** — Slice 5's sheet. */
  onPreview?: () => void
  /** **Add a note for the crew** — Slice 5's composer. */
  onAddNote?: () => void
  /** Board 14's **Edit the note** (asks *how far*). */
  /** A series visit's *Edit the note* (Board 14) — the visit's own note, edited where it lives (For the crew). */
  onEditSeriesNote?: (noteId: string | null) => void
  /** A photo opened — Slice 5's lightbox. */
  onPhoto?: (photoId: string) => void
  /** The lock banner's **Adjust** — Slice 6's popup. */
  onAdjust?: () => void
}

const tab = (read: VisitRead, key: string) => `/visits/${read.id}?tab=${key}`

// ---------------------------------------------------------------------------
// The whole Overview (desktop and iPad)
// ---------------------------------------------------------------------------

export function VisitOverview({ read, handlers, tablet = false, portrait = false }: {
  read: VisitRead
  handlers: OverviewHandlers
  /** Board 15: days as rows. */
  tablet?: boolean
  /** Board 15 portrait: the KPIs stack and Off the plan folds under *Money tab*. */
  portrait?: boolean
}) {
  const { overview } = read
  const closed = read.status === 'closed' || read.status === 'cancelled'
  const series = overview.series !== null
  const billed = overview.internal?.billedOnActuals ?? null
  const left = billed
    ? <BilledOnActualsCard billed={billed} onOpen={handlers.onOpen} />
    : !closed ? <ForCrewCard summary={overview.forCrew} handlers={handlers} series={series} /> : null
  const right = series ? null : <FromCrewCard read={read} summary={overview.fromCrew} handlers={handlers} />
  return (
    <div className="flex flex-col gap-4 leading-[normal] tabular-nums" data-visit-overview="">
      {read.header.note?.kind === 'closed' && <LockBanner read={read} onAdjust={handlers.onAdjust} />}
      {overview.series && <SeriesCardView read={read} card={overview.series} onOpen={handlers.onOpen} />}
      {!series && (overview.days.oneDay
        ? <OneDayCard read={read} handlers={handlers} />
        : <DaysCard read={read} handlers={handlers} rows={tablet} />)}
      <EstimateCardView read={read} card={overview.estimate} onOpen={handlers.onOpen} portrait={portrait} />
      {overview.hoursPerVisit && <HoursChartCard chart={overview.hoursPerVisit} />}
      {overview.ready && <ReadyCardView read={read} card={overview.ready} handlers={handlers} />}
      {(left || right) && !portrait && (
        <div className="grid grid-cols-2 items-start gap-4">
          {left ?? <span />}
          {right}
        </div>
      )}
      {portrait && right}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The lock banner — Board 12 C, Board 13 A
// ---------------------------------------------------------------------------

export function LockBanner({ read, onAdjust }: { read: VisitRead; onAdjust?: (() => void) | undefined }) {
  const note = read.header.note
  if (!note || note.kind !== 'closed') return null
  const oneDay = read.overview.days.oneDay
  return (
    <div className="flex items-center gap-2.5 rounded-[12px] border border-green-band-line bg-green-band-bg px-3.5 py-[11px] text-[12.5px] text-accent-dark"
      data-as-drawn="vp-12 C banner">
      <VisitGlyph name="lock" size={15} stroke={2} />
      <span>
        <b>{HEADER.closed.by(note.by.name, timeIn(read.timeZone, note.at, oneDay ? 'dateAmpm' : 'dayAmpm'))}</b>{' '}
        {oneDay ? HEADER.closed.lockedShort(note.jobReference) : HEADER.closed.locked(note.jobReference)}
      </span>
      <span className="flex-1" />
      {read.can.close && onAdjust && <SmallButton onClick={onAdjust}>{HEADER.closed.adjust}</SmallButton>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Days — §7.4
// ---------------------------------------------------------------------------

export function dayChipWords(chip: DayChip): string {
  switch (chip.kind) {
    case 'closed_out': return OVERVIEW.days.chip.closed_out
    case 'on_site': return OVERVIEW.days.chip.on_site
    case 'prep': return OVERVIEW.days.chip.prep(String(chip.ready), String(chip.total))
    case 'ready': return OVERVIEW.days.chip.ready
    case 'scheduled': return OVERVIEW.days.chip.scheduled
    case 'skipped': return OVERVIEW.days.chip.skipped
    case 'on_the_clock': return OVERVIEW.days.chip.on_site
  }
}

export function DayChipView({ chip }: { chip: DayChip }) {
  return (
    <StatusChip group="visitDay" status={chip.kind} className="shrink-0 whitespace-nowrap !gap-[5px]">
      {dayChipWords(chip)}
    </StatusChip>
  )
}

/** The big hours — *28.0 h* **of 27 planned** · *7.9 h* **so far of 27** · *22 h* **planned**. */
export function dayHours(tile: DayTile): { big: string; small: string } {
  const { hours } = tile
  if (hours.kind === 'actual') return { big: COMMON.h(hours1(hours.hours)), small: OVERVIEW.days.ofPlanned(hoursBare(hours.planned)) }
  if (hours.kind === 'so_far') return { big: COMMON.h(hours1(hours.hours)), small: OVERVIEW.days.soFarOf(hoursBare(hours.planned)) }
  return { big: COMMON.h(hoursBare(hours.hours)), small: OVERVIEW.days.planned }
}

export function dayWhen(read: VisitRead, tile: DayTile): string {
  const { when } = tile
  if (when.kind === 'worked') return COMMON.range(timeIn(read.timeZone, when.from, 'clock'), timeIn(read.timeZone, when.to, 'clock'))
  if (when.kind === 'on_site_since') return OVERVIEW.days.onSiteSince(timeIn(read.timeZone, when.at, 'clock'))
  return OVERVIEW.days.start(localTime(when.at))
}

function RelativeWord({ relative }: { relative: 'today' | 'tomorrow' }) {
  return (
    <span className={cn('text-[10.5px] font-bold uppercase tracking-[.04em]', relative === 'today' ? 'text-teal-band-text' : 'text-warn')}>
      {relative === 'today' ? COMMON.today : COMMON.tomorrow}
    </span>
  )
}

function DaysCard({ read, handlers, rows }: { read: VisitRead; handlers: OverviewHandlers; rows: boolean }) {
  const { days } = read.overview
  const words = COMMON.numberWords[days.count] ?? String(days.count)
  return (
    <BoardCard
      title={OVERVIEW.days.title}
      count={days.count}
      /* Board 15 (iPad): the title alone — no sub. */
      sub={rows ? undefined : OVERVIEW.days.sub(words)}
      action={days.scheduleDay ? { label: OVERVIEW.days.openSchedule, glyph: 'calendar', href: `/schedule?date=${days.scheduleDay}&visit=${read.id}` } : null}
      bodyClassName="p-3.5"
      asDrawn="visit-overview days"
    >
      <div className={cn('flex gap-2.5', rows && 'flex-col')}>
        {days.days.map((tile) => (
          <DayTileView key={tile.day.index} read={read} tile={tile} onOpen={() => handlers.onOpenDay?.(tile.day.index)} />
        ))}
        {days.canAddDay && (
          rows ? (
            /* Board 15: a 40 px dashed row, *+ Add a day* on one line. */
            <button type="button" onClick={handlers.onAddDay}
              className="box-content flex h-10 w-[calc(100%-3px)] items-center justify-center gap-1.5 rounded-[12px] border-[1.5px] border-dashed border-line-strong text-[12.5px] font-bold text-accent hover:bg-hover">
              <VisitGlyph name="plus" size={15} stroke={2.2} />
              {OVERVIEW.days.addDay}
            </button>
          ) : (
            <button
              type="button"
              onClick={handlers.onAddDay}
              className="box-border flex w-[118px] shrink-0 flex-col items-center justify-center gap-1 rounded-[12px] border-[1.5px] border-dashed border-line-strong text-accent hover:bg-hover"
            >
              <VisitGlyph name="plus" size={18} stroke={2.2} />
              <b className="text-[12.5px]">{OVERVIEW.days.addDay}</b>
              <span className="text-[11px] text-text-muted">{OVERVIEW.days.orMoveOne}</span>
            </button>
          )
        )}
      </div>
    </BoardCard>
  )
}

function DayTileView({ read, tile, onOpen }: { read: VisitRead; tile: DayTile; onOpen: () => void }) {
  const { big, small } = dayHours(tile)
  const today = tile.day.relative === 'today'
  return (
    <button
      type="button"
      onClick={onOpen}
      data-day-tile={tile.day.index}
      className={cn('box-border flex min-w-0 flex-1 flex-col gap-[7px] rounded-[12px] border border-line px-[13px] pb-3 pt-[11px] text-left hover:border-line-strong',
        today ? 'bg-green-band-bg' : 'bg-surface')}
    >
      <span className="flex items-center gap-1.5">
        <b className="whitespace-nowrap text-[13px] font-bold">{COMMON.dayN(String(tile.day.index))}</b>
        <span className="whitespace-nowrap text-[12px] text-text-muted">{dayText(tile.day.date, 'date')}</span>
        {tile.day.relative && <RelativeWord relative={tile.day.relative} />}
        <span className="flex-1" />
        {tile.forecast && <Forecast weather={tile.forecast} textSize={11} iconSize={13} dropSize={10} />}
      </span>
      <span className="flex items-center gap-1.5">
        <b className="text-[20px] font-bold tracking-[-.01em]">{big}</b>
        <span className="text-[12px] text-text-muted">{small}</span>
        <span className="flex-1" />
        <DayChipView chip={tile.chip} />
      </span>
      <EvBar bar={tile.bar} size="sm" />
      <span className="flex items-center gap-1.5">
        <FaceRow people={tile.crew} size={22} />
        <span className="text-[11.5px] text-text-muted">{dayWhen(read, tile)}</span>
        <span className="flex-1" />
        {tile.over && tile.over.reason && (
          <span className="whitespace-nowrap text-[11.5px] font-bold text-warn">
            {OVERVIEW.days.over(signed(tile.over.delta), tile.over.reason)}
          </span>
        )}
      </span>
    </button>
  )
}

/** Board 13 A: *Day · a one-day visit draws one row*. */
function OneDayCard({ read, handlers }: { read: VisitRead; handlers: OverviewHandlers }) {
  const { days } = read.overview
  const tile = days.days[0]
  return (
    <BoardCard title={ONE_DAY.title} sub={ONE_DAY.sub} bodyClassName="p-3.5" asDrawn="visit-overview one-day">
      {tile && (
        <button
          type="button"
          onClick={() => handlers.onOpenDay?.(tile.day.index)}
          className="flex w-full items-center gap-4 rounded-[12px] border border-line bg-surface px-[13px] py-[11px] text-left"
        >
          <span className="flex flex-col">
            <b className="text-[13px] font-bold">{dayText(tile.day.date, 'date')}</b>
            <span className="text-[11.5px] text-text-muted">
              {tile.when.kind === 'worked'
                ? ONE_DAY.when(timeIn(read.timeZone, tile.when.from, 'clock'), timeIn(read.timeZone, tile.when.to, 'clock'))
                : dayWhen(read, tile)}
            </span>
          </span>
          <span className="flex-1"><EvBar bar={tile.bar} size="sm" /></span>
          <span className="flex items-baseline gap-1.5">
            <b className="text-[18px] font-bold">{dayHours(tile).big}</b>
            <span className="text-[12px] text-text-muted">{ONE_DAY.ofPlanned(hoursBare(tile.hours.planned))}</span>
          </span>
          <FaceRow people={tile.crew} size={22} />
          <DayChipView chip={tile.chip} />
        </button>
      )}
      {days.canAddDay && (
        <button type="button" onClick={handlers.onAddDay}
          className="mt-2.5 box-border flex h-[46px] w-full items-center justify-center gap-1.5 rounded-[12px] border-[1.5px] border-dashed border-line-strong text-accent hover:bg-hover">
          <VisitGlyph name="plus" size={16} stroke={2.2} />
          <b className="text-[12.5px]">{OVERVIEW.days.addDay}</b>
        </button>
      )}
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// Estimate vs actual — §7.6, §6
// ---------------------------------------------------------------------------

/** A difference as the board's tag: amber over, green under (§3.2). */
export function DeltaTag({ delta, children }: { delta: Delta; children: ReactNode }) {
  return (
    <span
      className={cn('inline-flex h-[19px] items-center whitespace-nowrap rounded-[5px] px-1.5 text-[11px] font-bold',
        delta.tone === 'over' ? 'bg-warn-bg text-warn' : delta.tone === 'under' ? 'bg-accent-soft text-accent-dark' : 'bg-line-soft text-text-faint')}
    >
      {children}
    </span>
  )
}

function Kpi({ label, big, unit, bar, line, bottom }: {
  label: string; big: string; unit: string; bar: ReactNode; line: string; bottom: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
      <span className="text-label uppercase leading-[normal] text-text-faint">{label}</span>
      <span className="flex items-baseline gap-1.5">
        <b className="text-[24px] font-bold tracking-[-.02em]">{big}</b>
        <span className="text-[12.5px] text-text-muted">{unit}</span>
      </span>
      {bar}
      <span className="text-[12px] text-text-muted">{line}</span>
      <span className="flex items-center gap-2">{bottom}</span>
    </div>
  )
}

function Projected({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-[12.5px] text-text-body">
      {label}{' '}<b className="font-bold text-text">{value}</b>
    </span>
  )
}

function EstimateCardView({ read, card, onOpen, portrait }: { read: VisitRead; card: EstimateCard; onOpen: (href: string) => void; portrait: boolean }) {
  const internal = card.internal
  const final = card.mode === 'final'
  const seriesScope = card.scope === 'series'
  const tz = read.timeZone
  const sub = !internal ? OVERVIEW.estimate.hoursOnly
    : final ? OVERVIEW.estimate.final
      : seriesScope ? OVERVIEW.estimate.series
        : OVERVIEW.estimate.sub
  /*
   * D23 (§4.2): approved before plan lines, so there is nothing to be over or
   * under — *No estimate — approved before plan lines* where each *of …
   * estimated* would be, and no chip measuring against a zero (walkthrough
   * Oct 30, §9: *0.0 h so far of 0.0 h estimated*).
   */
  const none = card.noEstimate

  const hours = card.hours
  const hoursKpi = (
    <Kpi
      label={seriesScope ? OVERVIEW.estimate.thisVisit : OVERVIEW.estimate.hours}
      big={COMMON.h(hours1(hours.soFar))}
      unit={final ? OVERVIEW.estimate.actual : OVERVIEW.estimate.soFar}
      bar={<EvBar bar={hours.bar} size="md" tick={!none} />}
      line={none ? OVERVIEW.estimate.noEstimate
        : hours.onSiteSince
          ? OVERVIEW.estimate.ofHoursOnSiteSince(hoursBare(hours.estimated), timeIn(tz, hours.onSiteSince, 'clock'))
          : OVERVIEW.estimate.ofEstimated(COMMON.h(hours1(hours.estimated)))}
      bottom={final ? (
        !none && (
          <>
            {hours.delta.tone === 'under' && <span className="text-[12.5px] text-text-body">{OVERVIEW.estimate.finalUnder}</span>}
            <DeltaTag delta={hours.delta}>{COMMON.h(signed(hours.delta))}</DeltaTag>
          </>
        )
      ) : (
        <>
          <Projected label={OVERVIEW.estimate.projectedLabel} value={COMMON.h(hours1(hours.projected))} />
          {none ? null : hours.delta.tone === 'zero'
            ? <span className="text-[12px] text-text-muted">{OVERVIEW.estimate.asPlanned}</span>
            : <DeltaTag delta={hours.delta}>{COMMON.h(signed(hours.delta))}</DeltaTag>}
        </>
      )}
    />
  )

  if (!internal) {
    return (
      <BoardCard title={OVERVIEW.estimate.title} sub={sub} bodyClassName="px-[18px] pb-4 pt-3.5" asDrawn="vp-01 B estimate">
        <div className="w-[300px]">{hoursKpi}</div>
        {card.hoursOffThePlan.length > 0 && (
          <div className="mt-3.5">
            <b className="text-[13px] font-bold">{OVERVIEW.offPlan.title(String(card.hoursOffThePlan.length))}</b>
            <div className="mt-2">
              {card.hoursOffThePlan.map((row) => (
                <OffPlanLine key={row.day} glyph="people"
                  title={OVERVIEW.offPlan.ranOver(String(row.day), hours1(row.hours))}
                  line={row.by ? COMMON.dot(row.reason ?? '', row.by.name) : (row.reason ?? '')}
                  amount={COMMON.h(signed(row.delta))} tone={row.delta.tone} />
              ))}
            </div>
          </div>
        )}
      </BoardCard>
    )
  }

  const { cost, margin, series, offThePlan } = internal
  const costKpi = (
    <Kpi
      label={OVERVIEW.estimate.cost}
      big={money(cost.soFar)}
      unit={final ? OVERVIEW.estimate.actual : OVERVIEW.estimate.soFar}
      bar={<EvBar bar={cost.bar} size="md" tick={!none} />}
      line={none ? OVERVIEW.estimate.noEstimate : OVERVIEW.estimate.ofEstimated(money(cost.estimated))}
      bottom={final ? (
        !none && (
          <>
            {cost.delta.tone === 'under' && <span className="text-[12.5px] text-text-body">{OVERVIEW.estimate.finalUnder}</span>}
            <DeltaTag delta={cost.delta}>{signedMoney(cost.delta)}</DeltaTag>
          </>
        )
      ) : (
        <>
          {/* *—* until something costs anything — never *$0* (walkthrough Oct 30, §9). */}
          <Projected label={OVERVIEW.estimate.projectedLabel} value={cost.projected === null ? EM_DASH : money(cost.projected)} />
          {none || cost.projected === null ? null : cost.delta.tone === 'zero'
            ? <span className="text-[12px] text-text-muted">{OVERVIEW.estimate.asPlanned}</span>
            : <DeltaTag delta={cost.delta}>{signedMoney(cost.delta)}</DeltaTag>}
        </>
      )}
    />
  )
  const marginKpi = margin && (
    <Kpi
      label={OVERVIEW.estimate.margin}
      /* *—* until something costs anything — never *100.0%* (walkthrough Oct 30, §9). */
      big={margin.percent === null ? EM_DASH : percent(margin.percent)}
      unit={final ? OVERVIEW.estimate.actual : OVERVIEW.estimate.projected}
      bar={<MarginBar bar={margin.bar} tick={!none} />}
      line={none ? OVERVIEW.estimate.noEstimate
        : read.billing === 'time_materials' || final
          ? OVERVIEW.estimate.estimated(percent(margin.estimated))
          : OVERVIEW.estimate.estimatedOnFixed(percent(margin.estimated), money(margin.on))}
      bottom={final ? (
        <>
          <span className="text-[12.5px] text-text-body">{OVERVIEW.estimate.finalOnBilled(money(margin.on))}</span>
          {!none && margin.percent !== null && <DeltaTag delta={margin.delta}>{COMMON.pts(signed(margin.delta))}</DeltaTag>}
        </>
      ) : (
        <>
          {margin.earns !== null && <Projected label={OVERVIEW.estimate.earns} value={money(margin.earns)} />}
          {!none && margin.percent !== null && <DeltaTag delta={margin.delta}>{COMMON.pts(signed(margin.delta))}</DeltaTag>}
        </>
      )}
    />
  )
  const seriesKpi = series && (
    <Kpi
      label={OVERVIEW.estimate.seriesToDate}
      big={percent(series.percent)}
      unit={COMMON.margin}
      bar={<MarginBar bar={series.bar} />}
      line={OVERVIEW.estimate.seriesEstimated(percent(series.estimated), String(series.closedCount))}
      bottom={(
        <>
          <Projected label={OVERVIEW.estimate.billed} value={money(series.billed)} />
          <span className="text-[12px] text-text-muted">
            {OVERVIEW.estimate.ofBilled(money(series.total), String(series.billedCount), money(series.perVisit))}
          </span>
        </>
      )}
    />
  )
  const legendOver = hours.bar.projectedOver || cost.bar.projectedOver
  return (
    <BoardCard
      title={OVERVIEW.estimate.title}
      chip={<InternalChip />}
      sub={portrait ? undefined : sub}
      internal
      action={final || seriesScope ? null : { label: OVERVIEW.estimate.moneyTab, glyph: 'chevronRight', onClick: () => onOpen(tab(read, 'money')) }}
      bodyClassName="px-[18px] pb-4 pt-3.5"
      asDrawn="visit-overview estimate"
    >
      <div className={cn('flex', portrait ? 'flex-col gap-4' : 'gap-7')}>
        {hoursKpi}
        {costKpi}
        {marginKpi}
        {seriesKpi}
      </div>
      {!final && !seriesScope && (
        <div className="mt-2.5 flex justify-end">
          <div className="flex items-center gap-3.5">
            <LegendSwatch className="h-1.5 w-3.5 rounded-[9px] bg-accent">{OVERVIEW.estimate.legend.soFar}</LegendSwatch>
            <LegendSwatch className={cn('h-1.5 w-3.5 rounded-[9px]', legendOver ? 'bg-warn-line' : 'bg-accent-ring')}>
              {OVERVIEW.estimate.legend.stillToCome}
            </LegendSwatch>
            <LegendSwatch className="h-3 w-0.5 bg-text">{OVERVIEW.estimate.legend.estimate}</LegendSwatch>
          </div>
        </div>
      )}
      {offThePlan && !portrait && (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <b className="text-[13px] font-bold">{OVERVIEW.offPlan.title(String(offThePlan.count))}</b>
            <span className="text-[11.5px] text-text-muted">{OVERVIEW.offPlan.sub}</span>
          </div>
          <div className="mt-2">
            {offThePlan.rows.map((row, index) => <OffPlanRowView key={index} read={read} row={row} />)}
            <div className="flex items-center gap-[11px] border-t border-line pt-[9px]">
              <b className="flex-1 text-[13px] font-bold">{OVERVIEW.offPlan.total}</b>
              <Amount tone={offThePlan.total.tone}>{signedMoney(offThePlan.total)}</Amount>
            </div>
          </div>
        </div>
      )}
    </BoardCard>
  )
}

function LegendSwatch({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-[5px] text-[11px] text-text-muted">
      <i className={cn('inline-block', className)} />
      {children}
    </span>
  )
}

function Amount({ tone, children }: { tone: Delta['tone']; children: ReactNode }) {
  return (
    <span className={cn('whitespace-nowrap text-right font-bold',
      tone === 'over' ? 'text-warn' : tone === 'under' ? 'text-accent-dark' : 'text-text-faint')}>
      {children}
    </span>
  )
}

function OffPlanLine({ glyph, title, line, amount, tone }: {
  glyph: VisitGlyphName; title: string; line: string; amount: string; tone: Delta['tone']
}) {
  return (
    <div className="flex items-center gap-[11px] border-t border-line py-[9px]">
      <span className="box-content inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-button border border-line bg-surface text-text-muted">
        <VisitGlyph name={glyph} size={14} stroke={2} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <b className="text-[13px] font-semibold">{title}</b>
        <span className="text-[11.5px] text-text-muted">{line}</span>
      </div>
      <Amount tone={tone}>{amount}</Amount>
    </div>
  )
}

const OFF_PLAN_GLYPH: Record<OffThePlanRow['kind'], VisitGlyphName> = {
  swap: 'swap', receipt: 'receipt', fee: 'receipt', labor: 'people', equipment: 'loader', rental: 'truck',
  unplanned: 'material', other: 'info',
}

function OffPlanRowView({ read, row }: { read: VisitRead; row: OffThePlanRow }) {
  const amount = signedMoney(row.amount)
  const glyph = OFF_PLAN_GLYPH[row.kind]
  switch (row.kind) {
    case 'swap': {
      const to = row.line.detail && row.line.model ? `${row.line.model}, ${row.line.detail}` : row.line.model ?? row.line.name
      return (
        <OffPlanLine glyph={glyph} amount={amount} tone={row.amount.tone}
          title={OVERVIEW.offPlan.swapTitle(row.line.shortName, to)}
          line={OVERVIEW.offPlan.swapLine(row.from.noun, SWAP.reasonInSentence[row.reason as keyof typeof SWAP.reasonInSentence] ?? '',
            row.by.name, timeIn(read.timeZone, row.at, 'day'))} />
      )
    }
    case 'receipt':
      return (
        <OffPlanLine glyph={glyph} amount={amount} tone={row.amount.tone}
          title={OVERVIEW.offPlan.receiptTitle(row.vendor, row.line.noun)}
          line={OVERVIEW.offPlan.receiptLine(money(row.spent), money(row.planned), row.also ?? '')} />
      )
    case 'fee':
      return (
        <OffPlanLine glyph={glyph} amount={amount} tone={row.amount.tone}
          title={OVERVIEW.offPlan.feeTitle(row.name, String(row.day))}
          line={OVERVIEW.offPlan.feeLine(row.vendor, money(row.spent), money(row.planned))} />
      )
    case 'labor': {
      const day = row.days[0]
      return (
        <OffPlanLine glyph={glyph} amount={amount} tone={row.amount.tone}
          title={OVERVIEW.offPlan.labor}
          line={day
            ? OVERVIEW.offPlan.laborLine(String(day.index), hours1(day.hours), day.reason ?? '', money(row.crewRate), money(row.estimateRate))
            : ''} />
      )
    }
    case 'equipment':
    case 'rental':
    case 'unplanned':
      return <OffPlanLine glyph={glyph} amount={amount} tone={row.amount.tone} title={row.name} line={row.planned ? COMMON.dot(money(row.now), money(row.planned)) : money(row.now)} />
    case 'other':
      return <OffPlanLine glyph={glyph} amount={amount} tone={row.amount.tone} title={OVERVIEW.offPlan.total} line="" />
  }
}

// ---------------------------------------------------------------------------
// Ready for <next day> — §7.7
// ---------------------------------------------------------------------------

function ReadyMarkView({ mark }: { mark: ReadyRow['mark'] }) {
  if (mark === 'needs') {
    return (
      <span className="box-border inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-warn-line bg-warn-bg text-warn">
        <VisitGlyph name="warn" size={12} stroke={2.4} />
      </span>
    )
  }
  if (mark === 'on_the_way') {
    return (
      <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-info-bg text-info">
        <VisitGlyph name="clock" size={13} stroke={2.2} />
      </span>
    )
  }
  return (
    <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-accent text-white">
      <VisitGlyph name="check" size={12} stroke={3} />
    </span>
  )
}

/** A Ready row's words — title and the line under it (§7.7, Board 1). */
export function readyWords(read: VisitRead, row: ReadyRow): { title: string; line: string } {
  const tz = read.timeZone
  if (row.kind === 'material') {
    const first = row.lines[0]
    const name = first ? first.names.name : ''
    const qty = first ? (first.unit ? COMMON.qty(String(first.qty), first.qty === 1 ? first.unit.one : first.unit.many) : String(first.qty)) : ''
    if (row.how === 'to_order') {
      return {
        title: OVERVIEW.ready.notOrdered(name),
        line: row.neededBy && row.vendor
          ? (row.vendorStock !== null
            ? OVERVIEW.ready.neededBy(timeIn(tz, row.neededBy, 'clock'), timeIn(tz, row.neededBy, 'day'), row.vendor.name, String(row.vendorStock))
            : OVERVIEW.ready.neededByVendor(timeIn(tz, row.neededBy, 'clock'), timeIn(tz, row.neededBy, 'day'), row.vendor.name))
          : '',
      }
    }
    if (row.how === 'on_the_way') {
      const arrives = row.arrives
      return {
        title: OVERVIEW.ready.onTheWayToYard(first?.names.shortName ?? name, qty),
        line: arrives && row.po && row.vendor
          ? OVERVIEW.ready.delivers(row.po.number, row.vendor.name,
            arrives.relative === 'today' ? COMMON.today : arrives.relative === 'tomorrow' ? COMMON.tomorrow : timeIn(tz, arrives.at, 'day'),
            timeIn(tz, arrives.at, 'hourAmpm'))
          : '',
      }
    }
    if (row.how === 'pick_up') {
      const names = COMMON.list(row.lines.map((line) => line.names.name))
      const who = row.picker && row.pickupAt ? `${row.picker.name}, ${timeIn(tz, row.pickupAt, 'clock')}` : ''
      const parts = [row.vendor?.name ?? '', who, row.po?.number ?? ''].filter(Boolean)
      return {
        title: OVERVIEW.ready.pickUp(row.lines.length === 1 ? name : names, row.lines.length === 1 ? qty : ''),
        line: parts.reduce((a, b) => (a ? COMMON.dot(a, b) : b), ''),
      }
    }
    return {
      title: OVERVIEW.ready.load(row.lines.length === 1 ? name : COMMON.list(row.lines.map((line) => line.names.name))),
      line: row.held && row.loadsWith.length > 0 ? OVERVIEW.ready.heldWith(row.loadsWith[0]!.noun) : '',
    }
  }
  if (row.kind === 'rental') {
    return {
      title: OVERVIEW.ready.rented(row.description),
      line: OVERVIEW.ready.rentedLine(row.vendor.name, row.picker?.name ?? '', row.pickupAt ? timeIn(tz, row.pickupAt, 'clock') : '', row.confirmation ?? ''),
    }
  }
  if (row.kind === 'equipment') {
    const parts: string[] = []
    if (row.staysOnSite.length) parts.push(OVERVIEW.ready.staysOnSite(COMMON.list(row.staysOnSite.map((unit) => unit.tag))))
    if (row.fromYard.length) parts.push(COMMON.list(row.fromYard.map((unit) => unit.tag)))
    for (const decide of row.decide) {
      /* `at` is the server's `YARD_MORNING_AT`; the noun is the server's word (*a wet saw*). */
      parts.push(OVERVIEW.ready.decidePicked(decide.type.noun, localTime(decide.at), String(decide.free)))
    }
    if (row.noUnits.length) parts.push(COMMON.commaList(row.noUnits.map((type, index) => (index === 0 ? type.name : type.noun))))
    return { title: OVERVIEW.ready.companyEquipment, line: parts.reduce((a, b) => (a ? COMMON.dot(a, b) : b), '') }
  }
  return {
    title: OVERVIEW.ready.crew(row.crew?.name ?? ''),
    line: COMMON.dot(row.people.map((person) => person.name).join(', '), OVERVIEW.days.start(localTime(row.start))),
  }
}

export function ReadyRowRight({ row, handlers, phone = false }: { row: ReadyRow; handlers: OverviewHandlers; phone?: boolean }) {
  if (row.kind === 'material') {
    if (row.how === 'to_order') {
      return (
        <SmallButton primary glyph="cart" onClick={() => row.lines[0] && handlers.onOrder?.(row.lines[0].id)} className={phone ? 'mt-1.5 self-start' : ''}>
          {OVERVIEW.ready.order}
        </SmallButton>
      )
    }
    return phone ? null : (
      <StatusChip group="material" status={row.state} className="shrink-0 whitespace-nowrap !gap-[5px]">{MATERIALS.state[row.state]}</StatusChip>
    )
  }
  if (row.kind === 'rental') {
    if (row.state === 'to_book') {
      return <SmallButton primary onClick={() => handlers.onBook?.(row.rentalId)}>{OVERVIEW.ready.bookIt}</SmallButton>
    }
    return phone ? null : (
      <StatusChip group="visitDay" status={row.mark === 'ready' ? 'ready' : 'prep'} className="shrink-0 whitespace-nowrap !gap-[5px]">
        {RENTALS.state.booked}
      </StatusChip>
    )
  }
  if (row.kind === 'crew') return phone ? null : <FaceRow people={row.people} size={20} />
  return null
}

function ReadyCardView({ read, card, handlers }: { read: VisitRead; card: ReadyCard; handlers: OverviewHandlers }) {
  return (
    <BoardCard
      title={OVERVIEW.ready.title(dayText(card.day.date, 'weekday'))}
      sub={OVERVIEW.ready.sub(String(card.day.index), dayText(card.day.date, 'date'), localTime(card.start))}
      right={(
        <div className="flex items-center gap-3.5">
          <div className="flex items-center gap-2">
            <ReadyBar pct={card.readyPct} />
            <span className="whitespace-nowrap text-[12px] font-bold text-text">
              {OVERVIEW.ready.count(String(card.readyCount), String(card.total))}
            </span>
          </div>
          {/* Board 1: the link sits in the same 14 px row as the bar. */}
          <CardLink action={{ label: OVERVIEW.ready.loadList, glyph: 'chevronRight', href: `/visits/${read.id}?tab=materials&view=day` }} />
        </div>
      )}
      bodyClassName="px-[18px] pb-2 pt-1"
      asDrawn="vp-03 C ready"
    >
      {card.rows.map((row, index) => {
        const { title, line } = readyWords(read, row)
        return (
          <div key={index} data-ready-row={row.kind}
            className={cn('flex items-center gap-[11px] border-t border-line-faint py-[9px]',
              row.mark === 'needs' && '-mx-[18px] bg-amber-row px-[18px]')}>
            <ReadyMarkView mark={row.mark} />
            <div className="flex min-w-0 flex-1 flex-col">
              <b className="text-[13px] font-semibold">{title}</b>
              {line && <span className="text-[11.5px] text-text-muted">{line}</span>}
            </div>
            <ReadyRowRight row={row} handlers={handlers} />
          </div>
        )
      })}
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// For the crew · From the crew — §7.8
// ---------------------------------------------------------------------------

/** The visit's own note a series' *Edit the note* opens (Board 14) — its activity. */
function seriesNoteId(summary: ForCrewSummary): string | null {
  for (const note of summary.notes) if (note.home.kind === 'visit' && note.home.editable) return note.home.activityId
  return null
}

function ForCrewCard({ summary, handlers, series }: { summary: ForCrewSummary; handlers: OverviewHandlers; series: boolean }) {
  if (series) {
    return (
      <BoardCard title={OVERVIEW.forCrewCard.title} count={summary.count} bodyClassName="px-[18px] pb-3 pt-2">
        {summary.notes.map((note, index) => (
          <div key={note.id} className={cn('flex items-start gap-[9px] py-1.5', index > 0 && 'border-t border-line-faint')}>
            <SourceChip source={note.source} />
            <span className="text-[12.5px] leading-[1.45]">{note.text}</span>
          </div>
        ))}
        {summary.canAdd && (
          <div className="relative mt-1.5 flex justify-end">
            <button type="button" onClick={() => handlers.onEditSeriesNote?.(seriesNoteId(summary))}
              className="inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-button border border-line bg-surface px-3.5 text-[12px] font-semibold text-text hover:bg-hover"
              data-edit-series-note="">
              <VisitGlyph name="pencil" size={14} stroke={2} />
              {SERIES.editNote}
            </button>
          </div>
        )}
      </BoardCard>
    )
  }
  return (
    <BoardCard
      title={OVERVIEW.forCrewCard.title}
      count={summary.count}
      sub={OVERVIEW.forCrewCard.sub}
      action={handlers.onPreview ? { label: OVERVIEW.forCrewCard.preview, glyph: 'eye', onClick: handlers.onPreview } : null}
      bodyClassName="px-[18px] pb-3.5 pt-0.5"
      asDrawn="visit-overview for-crew"
    >
      {summary.notes.map((note) => (
        <div key={note.id} className="flex items-start gap-[9px] border-t border-line-faint py-2">
          <SourceChip source={note.source} />
          <div className="min-w-0 flex-1 text-[12.5px] leading-[1.45] text-text-body">
            {note.text}
            {note.source === 'visit' && note.by && <span className="text-text-faint">{' '}{FOR_CREW.by(note.by.name)}</span>}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2 border-t border-line-faint pt-2.5">
        <span className="inline-flex items-center gap-[5px] text-[12px] text-text-body">
          <span className="inline-flex text-text-muted"><VisitGlyph name="doc" size={13} stroke={2} /></span>
          {OVERVIEW.forCrewCard.files(String(summary.files))}
        </span>
        <span className="inline-flex items-center gap-[5px] text-[12px] text-text-body">
          <span className="inline-flex text-text-muted"><VisitGlyph name="map" size={13} stroke={2} /></span>
          {OVERVIEW.forCrewCard.mapLayers(String(summary.maps))}
        </span>
        <span className="flex-1" />
        {summary.canAdd && handlers.onAddNote && (
          <SmallButton glyph="plus" onClick={handlers.onAddNote}>{OVERVIEW.forCrewCard.add}</SmallButton>
        )}
      </div>
    </BoardCard>
  )
}

export function PhotoThumb({ photo, width, height, label, more, onOpen }: {
  photo: CrewPhoto; width: number; height: number; label: string | null; more?: number; onOpen?: () => void
}) {
  return (
    <button type="button" onClick={onOpen}
      className="relative inline-block shrink-0 overflow-hidden rounded-[9px] bg-line-soft"
      style={{ width, height }} data-photo={photo.id}>
      <img src={photo.file.url} alt="" className="h-full w-full object-cover" />
      {label && (
        <span className="absolute left-1.5 top-1.5 inline-flex h-[17px] items-center rounded-[5px] bg-surface/90 px-1.5 text-[10px] font-bold text-text">
          {label}
        </span>
      )}
      {more !== undefined && more > 0 && (
        <span className="absolute inset-0 flex items-center justify-center bg-scrim/55 text-[15px] font-bold text-white">
          {OVERVIEW.fromCrewCard.more(String(more))}
        </span>
      )}
    </button>
  )
}

function FromCrewCard({ read, summary, handlers }: { read: VisitRead; summary: FromCrewSummary; handlers: OverviewHandlers }) {
  const five = summary.photos.length >= 5
  const anyToday = summary.photos.some((photo) => photo.relative === 'today')
  const latest = summary.latest
  return (
    <BoardCard
      title={OVERVIEW.fromCrewCard.title}
      count={summary.count}
      sub={five ? OVERVIEW.fromCrewCard.sub : undefined}
      action={{ label: OVERVIEW.fromCrewCard.seeAll, glyph: 'chevronRight', onClick: () => handlers.onOpen(tab(read, 'from-crew')) }}
      bodyClassName={cn('px-[18px] pt-3.5', five ? 'pb-3.5' : 'pb-4')}
      asDrawn="visit-overview from-crew"
    >
      <div className="flex flex-col gap-2.5">
        {summary.photos.length > 0 && (
          <div className={cn('flex', five ? 'gap-1.5' : 'gap-2')}>
            {summary.photos.map((photo, index) => {
              const last = index === summary.photos.length - 1
              /* Only the newest carries *Today* (Board 1; `FromCrewSummary.photos`). */
              const label = index === 0 && photo.relative === 'today' ? OVERVIEW.fromCrewCard.today
                : !anyToday && photo.tag ? FROM_CREW.tag[photo.tag] : null
              return (
                <PhotoThumb key={photo.id} photo={photo} width={five ? 82 : 92} height={five ? 64 : 66}
                  label={last && summary.morePhotos > 0 ? null : label}
                  {...(last ? { more: summary.morePhotos } : {})}
                  onOpen={() => handlers.onPhoto?.(photo.id)} />
              )
            })}
          </div>
        )}
        {latest && (
          <div className="flex items-start gap-[9px] border-t border-line-faint pt-2.5">
            <Face person={latest.by} size={26} />
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-1.5">
                <b className="text-[12.5px] font-bold">{latest.by.name}</b>
                <span className="text-[11px] text-text-faint">
                  {latest.kind === 'closeout'
                    ? OVERVIEW.fromCrewCard.atCloseOut(timeIn(read.timeZone, latest.at, 'dayAmpm'))
                    : COMMON.atDay(timeIn(read.timeZone, latest.at, 'dayAmpm'), String(latest.day))}
                </span>
              </div>
              <span className="text-[12.5px] leading-[1.45] text-text-body">{latest.text}</span>
            </div>
          </div>
        )}
        {summary.toReview.map((receipt) => (
          <div key={receipt.id} className="flex items-center gap-[9px] rounded-[10px] border border-warn-line bg-amber-row px-[11px] py-[9px]">
            <span className="inline-flex text-warn"><VisitGlyph name="receipt" size={15} stroke={2} /></span>
            <span className="flex-1 text-[12.5px]">
              <b>{receipt.internal ? `${receipt.vendor} ${money(receipt.internal.amount)}` : receipt.vendor}</b>{' '}
              {OVERVIEW.fromCrewCard.receiptLine(receipt.sentBy.name, RECEIPTS.paidWithInSentence[receipt.paidWith as keyof typeof RECEIPTS.paidWithInSentence] ?? '')}
            </span>
            <SmallButton height={28} onClick={() => handlers.onOpen(`/visits/${read.id}?tab=money&receipt=${receipt.id}`)}>
              {OVERVIEW.fromCrewCard.review}
            </SmallButton>
          </div>
        ))}
      </div>
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// Time & materials — §17.4
// ---------------------------------------------------------------------------

function BilledOnActualsCard({ billed, onOpen }: { billed: BilledOnActuals; onOpen: (href: string) => void }) {
  return (
    <BoardCard title={TM_BILLED.title} sub={TM_BILLED.sub} bodyClassName="px-[18px] pb-3.5 pt-1" asDrawn="visit-overview billed">
      {billed.rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2.5 border-t border-line-faint py-2 text-[13px]">
          <span className="flex-1">{row.kind === 'labor' ? TM_BILLED.labor : row.name}</span>
          <span className="text-text-muted">
            {row.kind === 'labor' ? TM_BILLED.times(COMMON.h(hours1(row.hours)), money(row.rate))
              : row.kind === 'material' ? TM_BILLED.times(String(row.qty), money(row.unitPrice))
                : TM_BILLED.atCost}
          </span>
          <span className="whitespace-nowrap text-right text-[13px] font-bold">{money(row.amount)}</span>
        </div>
      ))}
      <div className="flex items-center gap-2.5 border-t border-line pt-2.5">
        <b className="flex-1 text-[13.5px] font-bold">{TM_BILLED.toInvoice}</b>
        <span className="text-[12px] text-text-muted">{TM_BILLED.estimated(money(billed.toInvoice.estimated))}</span>
        <span className="whitespace-nowrap text-right text-[14.5px] font-bold">{money(billed.toInvoice.actual)}</span>
      </div>
      {billed.invoiceDraftId && (
        <div className="mt-2.5 flex justify-end">
          <button type="button" onClick={() => onOpen(`/invoices/${billed.invoiceDraftId}`)}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-[9px] border border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark">
            <VisitGlyph name="receipt" size={14.5} stroke={2} />
            {TM_BILLED.openDraft}
          </button>
        </div>
      )}
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// A recurring visit — §18, Board 14
// ---------------------------------------------------------------------------

const DOT: Record<SeriesCard['dots'][number]['state'], string> = {
  done: 'h-[15px] w-[15px] bg-accent',
  skipped: 'h-[15px] w-[15px] border-2 border-amber-band-text',
  today: 'h-[19px] w-[19px] bg-teal-band-text shadow-[0_0_0_3px_rgb(var(--color-teal-band-line))]',
  to_go: 'h-[15px] w-[15px] bg-line-strong',
  cancelled: 'h-[15px] w-[15px] bg-line-soft',
}

function SeriesCardView({ read, card, onOpen }: { read: VisitRead; card: SeriesCard; onOpen: (href: string) => void }) {
  const { legend } = card
  const skipped = legend.skipped[0]
  const jobTab = `/jobs/${read.header.meta.job.id}?tab=visits`
  return (
    <BoardCard
      title={SERIES.title}
      sub={card.fromQuote ? SERIES.sub(String(card.count), card.fromQuote.reference) : undefined}
      action={{ label: SERIES.open, onClick: () => onOpen(jobTab) }}
      bodyClassName="px-[18px] pb-4 pt-3.5"
      asDrawn="visit-overview series"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-[7px]">
          {card.dots.map((dot) => (
            <button key={dot.index} type="button" title={String(dot.index)} onClick={() => onOpen(`/visits/${dot.visitId}`)}
              className={cn('box-border inline-block shrink-0 rounded-full', DOT[dot.state])} data-dot={dot.state} />
          ))}
        </div>
        <div className="flex items-center gap-4">
          <Legend className="bg-accent">{SERIES.done(String(legend.done))}</Legend>
          {skipped && (
            <Legend className="border-2 border-amber-band-text">
              {SERIES.skipped(String(legend.skipped.length), dayText(skipped.date, 'monthDay'), skipped.reason ?? '')}
            </Legend>
          )}
          {legend.todayIndex !== null && <Legend className="bg-teal-band-text">{SERIES.today(String(legend.todayIndex))}</Legend>}
          <Legend className="bg-line-strong">{SERIES.toGo(String(legend.toGo), legend.endsOn ? dayText(legend.endsOn, 'monthDay') : '')}</Legend>
        </div>
        <div className="flex items-center gap-2">
          {card.previous && (
            <button type="button" onClick={() => onOpen(`/visits/${card.previous!.visitId}`)}
              className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-accent hover:text-accent-dark">
              {SERIES.previous(String(card.previous.index), card.previous.date ? dayText(card.previous.date, 'date') : '',
                card.previous.status === 'closed' ? COMMON.closed : '')}
            </button>
          )}
          <span className="flex-1" />
          {card.next && (
            <button type="button" onClick={() => onOpen(`/visits/${card.next!.visitId}`)}
              className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-accent hover:text-accent-dark">
              {SERIES.next(String(card.next.index), card.next.date ? dayText(card.next.date, 'date') : '')}
            </button>
          )}
        </div>
      </div>
    </BoardCard>
  )
}

function Legend({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-text-muted">
      <i className={cn('box-border inline-block h-[9px] w-[9px] rounded-full', className)} />
      {children}
    </span>
  )
}

/** **Hours per visit** — the board's 900 × 168 chart; heights are the server's (`heightPct` of the 150 px plot). */
function HoursChartCard({ chart }: { chart: HoursPerVisitChart }) {
  const width = 900
  const plot = 150
  const slot = width / Math.max(chart.bars.length, 1)
  const barWidth = 22
  const y = (pct: number) => plot - (pct / 100) * plot
  return (
    <BoardCard
      title={SERIES.hours.title}
      sub={SERIES.hours.sub(hours1(chart.average), String(chart.doneCount))}
      right={chart.overCount > 0 ? <DeltaTag delta={{ signed: '+', tone: 'over' }}>{SERIES.hours.over(String(chart.overCount), String(chart.doneCount))}</DeltaTag> : null}
      bodyClassName="px-[18px] pb-3.5 pt-3.5"
      asDrawn="visit-overview hours-chart"
    >
      <div className="flex flex-col gap-2">
        <svg width={width} height={168} viewBox={`0 0 ${width} 168`} className="block max-w-full" aria-hidden>
          {chart.bars.map((bar, index) => {
            const x = index * slot + (slot - barWidth) / 2
            const labelled = chart.labels.includes(bar.index)
            return (
              <g key={bar.index}>
                {bar.state === 'skipped' ? (
                  <rect x={x} y={plot - 4} width={barWidth} height={4} rx={2} className="fill-amber-band-line" />
                ) : bar.state === 'today' ? (
                  <>
                    {bar.plannedPct !== null && (
                      <rect x={x} y={y(bar.plannedPct)} width={barWidth} height={plot - y(bar.plannedPct)} rx={4} className="fill-teal-band-bg" />
                    )}
                    <rect x={x} y={y(bar.heightPct)} width={barWidth} height={plot - y(bar.heightPct)} rx={4}
                      style={{ fill: 'rgb(var(--status-job-approved-fg))' }} />
                  </>
                ) : (
                  <rect x={x} y={y(bar.heightPct)} width={barWidth} height={plot - y(bar.heightPct)} rx={4}
                    className={bar.state === 'over' ? 'fill-warn-bar' : 'fill-accent'} />
                )}
                {labelled && (
                  <text x={x + barWidth / 2} y={164} fontSize={10} textAnchor="middle" className="fill-text-faint">{bar.index}</text>
                )}
              </g>
            )
          })}
          <line x1={0} y1={y(chart.estimatePct)} x2={width} y2={y(chart.estimatePct)} strokeDasharray="4 4" className="stroke-text" />
          <text x={width - 2} y={y(chart.estimatePct) - 5} fontSize={10.5} textAnchor="end" fontWeight={700} className="fill-text">
            {SERIES.hours.estimate(hoursBare(chart.estimate))}
          </text>
        </svg>
        <div className="flex items-center gap-3.5">
          <ChartKey className="bg-accent">{SERIES.hours.legend.atOrUnder}</ChartKey>
          <ChartKey className="bg-warn-bar">{SERIES.hours.legend.over}</ChartKey>
          <ChartKey style={{ background: 'rgb(var(--status-job-approved-fg))' }}>{SERIES.hours.legend.today}</ChartKey>
          <ChartKey className="bg-amber-band-line">{SERIES.hours.legend.skipped}</ChartKey>
        </div>
      </div>
    </BoardCard>
  )
}

function ChartKey({ className, style, children }: { className?: string; style?: React.CSSProperties; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-[5px] text-[11.5px] text-text-muted">
      <i className={cn('inline-block h-2.5 w-2.5 rounded-[3px]', className)} style={style} />
      {children}
    </span>
  )
}

